import React, { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "../../../../lib/queryKeys";
import {
  clientManagementApi,
  type BulkUploadConfirmResponse,
  type BulkUploadDelivery,
  type BulkUploadPreviewResponse,
  type BulkUploadStatus,
  type BulkUploadUserInput,
} from "../api/clientManagement";
import { SectionCard } from "./SectionCard";

interface BulkUserUploadCardProps {
  effectiveClientId: string | null;
  isCentralAdmin: boolean;
}

const SAMPLE_CSV = `email,first_name,last_name,company,requires_consent
jane.doe@cadentgas.com,Jane,Doe,Cadent Gas,true
john.smith@cadentgas.com,John,Smith,Cadent Gas,true
`;

const normalizeHeader = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, "_");

const parseBooleanCell = (
  raw: string,
  rowNumber: number,
): boolean | undefined => {
  const value = raw.trim().toLowerCase();
  if (!value) return undefined;
  if (["true", "1", "yes", "y"].includes(value)) return true;
  if (["false", "0", "no", "n"].includes(value)) return false;
  throw new Error(`Row ${rowNumber}: requires_consent must be true or false.`);
};

const parseCsvMatrix = (csv: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = "";
  let inQuotes = false;

  for (let index = 0; index < csv.length; index += 1) {
    const char = csv[index];
    const nextChar = csv[index + 1];

    if (inQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          currentCell += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        currentCell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }

    if (char === "\n") {
      currentRow.push(currentCell);
      rows.push(currentRow);
      currentRow = [];
      currentCell = "";
      continue;
    }

    if (char === "\r") {
      continue;
    }

    currentCell += char;
  }

  currentRow.push(currentCell);
  rows.push(currentRow);

  while (rows.length > 0 && rows[rows.length - 1].every((cell) => !cell.trim())) {
    rows.pop();
  }

  return rows;
};

const parseUsersCsv = (csvText: string): BulkUploadUserInput[] => {
  const matrix = parseCsvMatrix(csvText);
  if (matrix.length < 2) {
    throw new Error("CSV must include a header row and at least one user row.");
  }

  const header = matrix[0].map(normalizeHeader);
  const emailIndex = header.indexOf("email");
  const firstNameIndex = header.indexOf("first_name");
  const lastNameIndex = header.indexOf("last_name");
  const companyIndex = header.indexOf("company");
  const requiresConsentIndex = header.indexOf("requires_consent");

  if (emailIndex === -1 || firstNameIndex === -1 || lastNameIndex === -1) {
    throw new Error(
      "CSV must include headers: email, first_name, last_name (company and requires_consent are optional).",
    );
  }

  const users: BulkUploadUserInput[] = [];

  for (let rowIndex = 1; rowIndex < matrix.length; rowIndex += 1) {
    const rowNumber = rowIndex + 1;
    const row = matrix[rowIndex];
    const readValue = (columnIndex: number) =>
      columnIndex >= 0 ? (row[columnIndex] ?? "").trim() : "";

    const email = readValue(emailIndex);
    const firstName = readValue(firstNameIndex);
    const lastName = readValue(lastNameIndex);
    const company = readValue(companyIndex);
    const requiresConsentRaw = readValue(requiresConsentIndex);

    if (!email && !firstName && !lastName && !company && !requiresConsentRaw) {
      continue;
    }

    if (!email || !firstName || !lastName) {
      throw new Error(
        `Row ${rowNumber}: email, first_name, and last_name are required.`,
      );
    }

    const requiresConsent = parseBooleanCell(requiresConsentRaw, rowNumber);
    users.push({
      email,
      first_name: firstName,
      last_name: lastName,
      company: company || undefined,
      ...(requiresConsent !== undefined
        ? { requires_consent: requiresConsent }
        : {}),
    });
  }

  if (!users.length) {
    throw new Error("CSV contains no valid user rows.");
  }

  return users;
};

const downloadTextFile = (
  fileName: string,
  content: string,
  mimeType: string,
) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const formatStatusLabel = (status: BulkUploadStatus) =>
  status.replace(/_/g, " ");

const statusToColor = (
  status: BulkUploadStatus,
): "success" | "warning" | "error" | "default" => {
  if (status === "ready") return "success";
  if (status === "duplicate_in_file" || status === "exists_same_client") {
    return "warning";
  }
  if (status === "invalid" || status === "exists_other_client") {
    return "error";
  }
  return "default";
};

export const BulkUserUploadCard: React.FC<BulkUserUploadCardProps> = ({
  effectiveClientId,
  isCentralAdmin,
}) => {
  const queryClient = useQueryClient();
  const [users, setUsers] = useState<BulkUploadUserInput[]>([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [previewData, setPreviewData] =
    useState<BulkUploadPreviewResponse["data"] | null>(null);
  const [confirmData, setConfirmData] =
    useState<BulkUploadConfirmResponse["data"] | null>(null);
  const [delivery, setDelivery] = useState<BulkUploadDelivery>("csv");
  const [loginUrl, setLoginUrl] = useState(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/login`;
  });

  const readyCount = previewData?.summary.ready ?? 0;

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveClientId) {
        throw new Error("Select a client first.");
      }
      return clientManagementApi.previewBulkUserUpload(effectiveClientId, users);
    },
    onSuccess: (response) => {
      setPreviewData(response.data);
      setConfirmData(null);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveClientId) {
        throw new Error("Select a client first.");
      }
      return clientManagementApi.confirmBulkUserUpload(effectiveClientId, {
        users,
        delivery,
        ...(delivery === "email" ? { login_url: loginUrl.trim() } : {}),
      });
    },
    onSuccess: (response) => {
      setConfirmData(response.data);
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.centralUsers("all") });

      if (delivery === "csv" && response.data.password_csv) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        downloadTextFile(
          `bulk-user-passwords-${timestamp}.csv`,
          response.data.password_csv,
          "text/csv;charset=utf-8",
        );
      }
    },
  });

  const handleFileChanged = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    try {
      const csvText = await file.text();
      const parsedUsers = parseUsersCsv(csvText);
      setFileName(file.name);
      setUsers(parsedUsers);
      setParseError(null);
      setPreviewData(null);
      setConfirmData(null);
    } catch (error) {
      setFileName(file.name);
      setUsers([]);
      setPreviewData(null);
      setConfirmData(null);
      setParseError(error instanceof Error ? error.message : "Failed to parse CSV.");
    }
  };

  if (!isCentralAdmin) {
    return (
      <SectionCard
        title="Bulk Upload Users"
        description="Bulk upload is available to central admins."
      >
        <Alert severity="info">
          Central admin access is required to run a bulk user upload.
        </Alert>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Bulk Upload Users"
      description="Upload a CSV, validate the rows, then confirm user creation in DB and Keycloak."
    >
      <Stack spacing={2}>
        {!effectiveClientId && (
          <Alert severity="info">
            Select a client before uploading users.
          </Alert>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button component="label" variant="outlined">
            Upload CSV
            <input
              type="file"
              hidden
              accept=".csv,text/csv"
              onChange={handleFileChanged}
            />
          </Button>
          <Button
            variant="text"
            onClick={() =>
              downloadTextFile(
                "bulk-user-upload-sample.csv",
                SAMPLE_CSV,
                "text/csv;charset=utf-8",
              )
            }
          >
            Download Sample CSV
          </Button>
        </Stack>

        {!!fileName && (
          <Typography variant="body2" color="text.secondary">
            File: {fileName} ({users.length} rows parsed)
          </Typography>
        )}

        {parseError && <Alert severity="error">{parseError}</Alert>}

        {previewMutation.isError && (
          <Alert severity="error">
            {previewMutation.error instanceof Error
              ? previewMutation.error.message
              : "Failed to validate upload."}
          </Alert>
        )}

        {confirmMutation.isError && (
          <Alert severity="error">
            {confirmMutation.error instanceof Error
              ? confirmMutation.error.message
              : "Failed to confirm upload."}
          </Alert>
        )}

        <Button
          variant="contained"
          disabled={
            !effectiveClientId ||
            users.length === 0 ||
            previewMutation.isPending ||
            confirmMutation.isPending
          }
          onClick={() => previewMutation.mutate()}
        >
          {previewMutation.isPending ? "Validating..." : "Validate Upload"}
        </Button>

        {previewData && (
          <Stack spacing={2}>
            <Alert severity={readyCount > 0 ? "success" : "warning"}>
              {previewData.summary.total} rows checked. Ready: {previewData.summary.ready}
              , Invalid: {previewData.summary.invalid}, Duplicates:{" "}
              {previewData.summary.duplicate_in_file}, Existing on this client:{" "}
              {previewData.summary.exists_same_client}, Existing on other clients:{" "}
              {previewData.summary.exists_other_client}.
            </Alert>

            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>First name</TableCell>
                    <TableCell>Last name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Consent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.rows.map((row) => (
                    <TableRow key={`${row.row_number}-${row.email}`}>
                      <TableCell>{row.row_number}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.first_name}</TableCell>
                      <TableCell>{row.last_name}</TableCell>
                      <TableCell>{row.company || "-"}</TableCell>
                      <TableCell>{row.requires_consent ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={statusToColor(row.status)}
                          label={formatStatusLabel(row.status)}
                        />
                      </TableCell>
                      <TableCell>{row.message || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack spacing={1}>
              <Typography variant="subtitle2">
                Password Delivery
              </Typography>
              <RadioGroup
                row
                value={delivery}
                onChange={(event) =>
                  setDelivery(event.target.value as BulkUploadDelivery)
                }
              >
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label="Email passwords to users"
                />
                <FormControlLabel
                  value="csv"
                  control={<Radio />}
                  label="Download passwords as CSV"
                />
              </RadioGroup>
              {delivery === "email" && (
                <TextField
                  label="Login URL for email template"
                  size="small"
                  value={loginUrl}
                  onChange={(event) => setLoginUrl(event.target.value)}
                  placeholder="https://app.example.com/login"
                  fullWidth
                />
              )}
            </Stack>

            <Button
              variant="contained"
              color="primary"
              disabled={
                readyCount === 0 ||
                confirmMutation.isPending ||
                (delivery === "email" && !loginUrl.trim())
              }
              onClick={() => confirmMutation.mutate()}
            >
              {confirmMutation.isPending
                ? "Creating users..."
                : `Confirm and Create ${readyCount} Users`}
            </Button>
          </Stack>
        )}

        {confirmData && (
          <Alert severity={confirmData.failed.length ? "warning" : "success"}>
            Created: {confirmData.summary.created}, Failed: {confirmData.summary.failed}
            , Skipped: {confirmData.summary.skipped}.
            {delivery === "csv" ? " Password CSV downloaded." : ""}
          </Alert>
        )}
      </Stack>
    </SectionCard>
  );
};
